// @ts-check
import {
  Box,
  Button,
  ChevronDownIcon,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from '~/design-system';
import { Trans } from '@lingui/macro';
import React from 'react';
import BrandLogo from '../BrandLogo';

/**
 * Renders the map suppliers page
 * @param {Object} props
 * @param {String[]} props.suppliers - the list of suppliers defined on the imported file
 * @param {Object[]} props.selectedSupplier - the selected supplier
 * @param {Object} props.suppliersMap - the supplier mapped to partner id
 * @param {function} props.onSelectSupplier - callback when match or rematch is clicked
 * @param {Object[]} props.importToolPartners - the list of current brand partners
 * @param {Object} props.rest
 */
export default function SuppliersTable({
  suppliers,
  selectedSupplier,
  suppliersMap,
  onSelectSupplier,
  importToolPartners,
  ...rest
}) {
  return (
    <Table
      sx={{
        marginBottom: 5,
        border: '1px solid',
        borderColor: 'brand.lightGray',
      }}
      {...rest}
    >
      <TableHead>
        <TableRow>
          <TableHeader
            sx={{
              width: 160,
              backgroundColor: 'brand.lightGray',
            }}
          />
          <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 220 }}>
            <Text variant="smallest">
              <Trans>Name in import</Trans>
            </Text>
          </TableHeader>
          <TableHeader sx={{ backgroundColor: 'brand.lightGray', width: 220 }}>
            <Text variant="smallest">
              <Trans>Name in Bottlebooks</Trans>
            </Text>
          </TableHeader>
          <TableHeader
            sx={{ backgroundColor: 'brand.lightGray', width: 100 }}
          />
        </TableRow>
      </TableHead>
      <TableBody>
        {suppliers?.length > 0 ? (
          suppliers?.map((supplier) => (
            <SupplierRow
              key={supplier}
              supplier={supplier}
              suppliersMap={suppliersMap}
              importToolPartners={importToolPartners}
              selectedSupplier={selectedSupplier}
              onSelectSupplier={onSelectSupplier}
            />
          ))
        ) : (
          <EmptyRow />
        )}
      </TableBody>
    </Table>
  );
}

function EmptyRow({ ...rest }) {
  return (
    <TableRow sx={{ height: '100px' }} {...rest}>
      <TableCell colSpan={4}>
        <Text>
          <Trans>No results</Trans>
        </Text>
      </TableCell>
    </TableRow>
  );
}

function SupplierRow({
  supplier,
  suppliersMap,
  importToolPartners,
  selectedSupplier,
  onSelectSupplier,
}) {
  const importToolPartnersIndex = importToolPartners?.reduce((acc, partner) => {
    acc[partner.partnerId] = partner;
    return acc;
  }, {});
  const [showMore, setShowMore] = React.useState(false);

  const partnerId = suppliersMap?.[supplier];
  const partner = importToolPartnersIndex[partnerId];

  return (
    <TableRow
      sx={{
        height: '100px',
        ':hover': {
          backgroundColor: supplier === selectedSupplier ? 'selected' : 'light',
        },
        backgroundColor: supplier === selectedSupplier ? 'selected' : '',
      }}
    >
      <TableCell sx={{ paddingLeft: '20px', paddingRight: '20px' }}>
        <BrandLogo brand={partner?.brand} />
      </TableCell>
      <TableCell sx={{ textAlign: 'left' }}>{supplier}</TableCell>
      <TableCell sx={{ textAlign: 'left' }}>
        <Box>{partner?.brand?.name}</Box>
        <Box>
          {partner ? (
            <Link
              onClick={() => {
                setShowMore(!showMore);
                console.log('todo: show more...');
              }}
            >
              {showMore ? <Trans>Show less</Trans> : <Trans>Show more</Trans>}
              <ChevronDownIcon />
            </Link>
          ) : (
            false && 'N/A'
          )}
          {showMore && (
            <React.Fragment>
              <Text variant="smallest" sx={{}}>
                Company ID: {partner?.companyId}
              </Text>
            </React.Fragment>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ height: '65px' }}>
        {!partner && (
          <Button
            variant="primary"
            sx={{ marginRight: 3 }}
            onClick={() => onSelectSupplier(supplier)}
          >
            <Trans>Match</Trans>
          </Button>
        )}
        {partner && (
          <Link
            sx={{ marginRight: 3 }}
            onClick={() => onSelectSupplier(supplier)}
          >
            <Trans>Rematch</Trans>
          </Link>
        )}
      </TableCell>
    </TableRow>
  );
}
